import React, { useEffect, useState } from "react";
import { getInquiryByClientId, getTicketCountsByAgentId } from "../../../services/AgentServices";

const isAssignedInquiryForUser = (inquiry, userId) => {
  const normalizedUserId = Number.parseInt(userId);
  const assignedId = Number.parseInt(inquiry?.assigned_to ?? inquiry?.assignedTo, 10);
  return assignedId === normalizedUserId;
};

const normalizeStatus = (status) => String(status || "").toUpperCase().trim();
const normalizeStatusCode = (status) => {
  const value = normalizeStatus(status);
  if (["OPEN", "OPN"].includes(value)) return "OPN";
  if (["IN-PROGRESS", "INPROGRESS", "IN_PROGRESS", "INP"].includes(value)) return "INP";
  if (["RESOLVED", "CLOSED", "CRS", "DONE"].includes(value)) return "CRS";
  if (["NO-RESPONSE", "NO RESPONSE", "NORESPONSE", "CNR"].includes(value)) return "CNR";
  return value;
};

export default function HelpSection() {
  const [loading, setLoading] = useState(true);
  const [hasTickets, setHasTickets] = useState(false);
  const [hasInquiries, setHasInquiries] = useState(false);

  useEffect(() => {
    const fetchCounts = async () => {
      const userId = sessionStorage.getItem("userId");
      const clientId = sessionStorage.getItem("clientId");

      if (!userId || !clientId) {
        setHasTickets(false);
        setHasInquiries(false);
        setLoading(false);
        return;
      }

      try {
        const [ticketCountRes, inquiryRes] = await Promise.all([
          getTicketCountsByAgentId(userId),
          getInquiryByClientId(clientId, 1, 1000),
        ]);

        const ticketCountData = ticketCountRes?.data || {};
        const ticketTotal =
          ticketCountData.totalCount ??
          (ticketCountData.openCount || 0) +
            (ticketCountData.inProgressCount || 0) +
            (ticketCountData.closedCount || 0) +
            (ticketCountData.noResponseCount || 0);

        const allInquiries =
          inquiryRes && (inquiryRes.status || inquiryRes.success) && Array.isArray(inquiryRes.data)
            ? inquiryRes.data
            : [];
        const assignedInquiries = allInquiries.filter((inq) => isAssignedInquiryForUser(inq, userId));
        const hasAnyInquiry = assignedInquiries.some((inq) =>
          ["OPN", "INP", "CRS", "CNR"].includes(normalizeStatusCode(inq.status || inq.status_code))
        );

        setHasTickets((ticketTotal || 0) > 0);
        setHasInquiries(hasAnyInquiry || assignedInquiries.length > 0);
      } catch (error) {
        setHasTickets(true);
        setHasInquiries(true);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, []);

  return (
    <div className="container my-5">
      <div className="mb-4">
        <h2 className="text-primary mb-2">Support Desk Help</h2>
        <p className="text-muted mb-0">
          Quick, guided steps for a smooth daily workflow.
        </p>
      </div>

      <div className="row g-4">
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Start Here (Daily Workflow)</h5>
            </div>
            <div className="card-body">
              <ol className="mb-0">
                <li>Open the Dashboard to review your assigned counts.</li>
                <li>Go to the work list to see your items.</li>
                <li>Open a record, respond to the customer, and update status.</li>
                <li>Set a follow-up date when needed.</li>
                <li>Mark items Resolved after completion.</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Dashboard Overview</h5>
            </div>
            <div className="card-body">
              <p className="mb-2">
                The Dashboard shows a quick summary of your assigned work.
              </p>
              <ul className="mb-0">
                <li>Charts are interactive and open filtered lists.</li>
                <li>Summary cards show open and resolved counts at a glance.</li>
                <li>Sections appear only when items exist to keep focus.</li>
              </ul>
            </div>
          </div>
        </div>

        {!loading && hasTickets && (
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-light">
                <h5 className="mb-0">Tickets: How To Manage</h5>
              </div>
              <div className="card-body">
                <ul className="mb-2">
                  <li>Open a ticket to read the conversation and reply.</li>
                  <li>Update status after each action.</li>
                  <li>Use filters to focus on priority items.</li>
                </ul>
                <div className="small text-muted">
                  Status codes: <code>OPN</code> Open, <code>INP</code> In Progress, <code>CRS</code> Resolved, <code>CNR</code> No Response.
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && hasInquiries && (
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header bg-light">
                <h5 className="mb-0">Inquiries: How To Manage</h5>
              </div>
              <div className="card-body">
                <ul className="mb-2">
                  <li>Open an inquiry to view details and respond.</li>
                  <li>Use status updates to track progress and outcomes.</li>
                  <li>Follow up on pending inquiries daily.</li>
                </ul>
                <div className="small text-muted">
                  Use date filters to see Today, Last 7 Days, or a Custom Range.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Follow-ups</h5>
            </div>
            <div className="card-body">
              <ul className="mb-0">
                <li>Set a follow-up date when you need to check back later.</li>
                <li>Review todays follow-ups and close the loop.</li>
                <li>Update status after follow-up completion.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-light">
              <h5 className="mb-0">Need Help?</h5>
            </div>
            <div className="card-body">
              <p className="mb-2">Contact support for urgent issues or access problems.</p>
              <div className="mb-1"><strong>Phone:</strong> +91 99609 14019</div>
              <div><strong>Email:</strong> <a href="mailto:info@ataibot.in">info@ataibot.in</a></div>
            </div>
          </div>
        </div>

        {!loading && !hasTickets && !hasInquiries && (
          <div className="col-12">
            <div className="alert alert-info mb-0">
              Your workspace is ready. New tickets or inquiries will appear here once they are assigned.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
